import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { getCurrentUserCompanyId } from '../utils/userUtils';

export default function ViewReports({ form, onBack }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    loadSubmissions();
  }, [form]);

  async function loadSubmissions() {
    setLoading(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        console.error('No company ID found');
        return;
      }

      const submissionsRef = collection(db, `companies/${companyId}/submissions`);
      const q = query(
        submissionsRef,
        where('formId', '==', form.id),
        orderBy('submittedAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const subs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setSubmissions(subs);
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setLoading(false);
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleString();
    }
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleString();
    }
    return new Date(timestamp).toLocaleString();
  };

  const formatAnswer = (answer) => {
    if (!answer) return '—';
    if (typeof answer === 'object' && answer.value !== undefined) {
      return String(answer.value);
    }
    if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
    if (Array.isArray(answer)) return answer.join(', ');
    return String(answer);
  };

  if (selectedSubmission) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setSelectedSubmission(null)}
            className="mb-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            ← Back to List
          </button>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">{form.title || 'Submission Details'}</h2>
            <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              <p>Submitted: {formatDate(selectedSubmission.submittedAt)}</p>
              {selectedSubmission.documentNumber && (
                <p>Document #: {selectedSubmission.documentNumber}</p>
              )}
            </div>
            
            <div className="space-y-4">
              {selectedSubmission.answers && Object.entries(selectedSubmission.answers).map(([key, value]) => {
                const label = value?.label || key;
                const answer = value?.value !== undefined ? value.value : value;
                
                return (
                  <div key={key} className="border-b pb-3">
                    <div className="font-semibold text-gray-700 dark:text-gray-300">{label}</div>
                    <div className="text-gray-600 dark:text-gray-400">{formatAnswer(answer)}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={onBack}
          className="mb-4 px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          ← Back to Dashboard
        </button>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold mb-6">Reports: {form.title}</h2>
          
          {loading ? (
            <div className="text-center py-8">Loading submissions...</div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No submissions found for this form.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Document #</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map(sub => (
                    <tr key={sub.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="py-3 px-4">{formatDate(sub.submittedAt)}</td>
                      <td className="py-3 px-4">{sub.documentNumber || '—'}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded text-sm">
                          {sub.status || 'Completed'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => setSelectedSubmission(sub)}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
